import { useCallback, useContext, useState } from 'react'
import toast from 'react-hot-toast/headless'
import { useTranslation } from 'react-i18next'
import api from '../../api'
import './DataSchema.css'
import { Table, useSearchTableState } from '../../components'
import { ProjectContext } from '../../contexts'
import { AllowedEvent, RulePath } from '../../types'
import { Button, Heading, Modal, Tabs } from '../../ui'
import FormWrapper from '../../ui/form/FormWrapper'
import RadioInput from '../../ui/form/RadioInput'
import TextInput from '../../ui/form/TextInput'
import { checkProjectRole, snakeToTitle } from '../../utils'
import { RestartIcon } from '../../ui/icons'

function UserProperties() {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const state = useSearchTableState(useCallback(async params => await api.data.userPaths.search(project.id, params), [project]))
    const [editing, setEditing] = useState<null | Partial<RulePath>>(null)
    const handleRebuildAttributeSchema = async () => {
        if (!confirm(t('data_schema_rebuild_confirm'))) return
        await api.data.rebuild(project.id)
        toast.success(t('data_schema_rebuild_success'))
    }

    return (
        <>
            <Table
                {...state}
                columns={[
                    { key: 'path', title: t('path') },
                    {
                        key: 'type',
                        title: t('type'),
                        cell: ({ item }) => snakeToTitle(item.type),
                    },
                    {
                        key: 'data_type',
                        title: t('data_type'),
                        cell: ({ item }) => snakeToTitle(item.data_type),
                    },
                    {
                        key: 'visibility',
                        title: t('visibility'),
                        cell: ({ item }) => snakeToTitle(item.visibility),
                    },
                ]}
                itemKey={({ item }) => item.id}
                onSelectRow={(row) => setEditing(row)}
                title={t('user_properties')}
                description={t('data_schema_description')}
                actions={
                    <Button
                        variant="secondary"
                        icon={<RestartIcon />}
                        onClick={async () => await handleRebuildAttributeSchema()
                        }
                    >{t('data_schema_rebuild')}</Button>
                }
                enableSearch
            />
            <Modal
                title={editing?.path}
                open={Boolean(editing)}
                onClose={() => setEditing(null)}
            >
                {
                    editing && (
                        <FormWrapper<RulePath>
                            onSubmit={
                                async ({ id, visibility }) => {
                                    await api.data.userPaths.update(project.id, id, { visibility })
                                    await state.reload()
                                    setEditing(null)
                                }
                            }
                            defaultValues={editing}
                        >
                            { form => <>
                                <RadioInput.Field
                                    form={form}
                                    name="visibility"
                                    label={t('visibility')}
                                    options={[
                                        { key: 'public', label: t('visibility_public') },
                                        { key: 'hidden', label: t('visibility_hidden') },
                                        { key: 'classified', label: t('visibility_classified') },
                                    ]}
                                />
                            </>}
                        </FormWrapper>
                    )
                }
            </Modal>
        </>
    )
}

function AllowedEvents() {
    const { t } = useTranslation()
    const [project, setProject] = useContext(ProjectContext)
    const state = useSearchTableState(useCallback(async params => await api.data.allowedEvents.search(project.id, params), [project]))
    const [newEventName, setNewEventName] = useState('')

    const handleToggle = async () => {
        const updated = await api.projects.patch(project.id, {
            event_whitelist_enabled: !project.event_whitelist_enabled,
        })
        setProject(updated)
    }

    const handleAdd = async () => {
        const name = newEventName.trim()
        if (!name) return
        await api.data.allowedEvents.create(project.id, name)
        setNewEventName('')
        await state.reload()
    }

    const handleDelete = async (event: AllowedEvent) => {
        await api.data.allowedEvents.delete(project.id, event.id)
        await state.reload()
    }

    return (
        <>
            <Table
                {...state}
                columns={[
                    { key: 'name', title: t('name') },
                    {
                        key: 'created_at',
                        title: t('created_at'),
                    },
                    {
                        key: 'delete',
                        title: '',
                        cell: ({ item }) => (
                            <Button
                                variant="destructive"
                                size="small"
                                onClick={async (e) => {
                                    e.stopPropagation()
                                    await handleDelete(item)
                                }}
                            >{t('delete')}</Button>
                        ),
                    },
                ]}
                itemKey={({ item }) => item.id}
                title={t('event_whitelist')}
                description={t('event_whitelist_description')}
                actions={
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={project.event_whitelist_enabled ?? false}
                            onChange={handleToggle}
                            disabled={!checkProjectRole('admin', project.role)}
                        />
                        <div className="slider round"></div>
                    </label>
                }
                enableSearch
            />
            {project.event_whitelist_enabled && (
                <form
                    className="event-whitelist-add"
                    onSubmit={async (e) => {
                        e.preventDefault()
                        await handleAdd()
                    }}
                >
                    <TextInput
                        name="event_name"
                        value={newEventName}
                        onChange={setNewEventName}
                        placeholder={t('event_whitelist_add_placeholder')}
                    />
                    <Button type="submit">{t('add_event')}</Button>
                </form>
            )}
        </>
    )
}

export default function DataSchema() {
    const { t } = useTranslation()

    return (
        <>
            <Heading size="h3" title={t('data_schema')} />
            <Tabs tabs={[
                {
                    key: 'user_properties',
                    label: t('user_properties'),
                    children: <UserProperties />,
                },
                {
                    key: 'events',
                    label: t('events'),
                    children: <AllowedEvents />,
                },
            ]} />
        </>
    )
}
